import React, { useEffect, useRef, useState } from "react";
import { shippingService } from "../services/dataService";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

export interface ShipmentPrintModalProps {
  shipmentId: string;
  number: string;
  /** Sevkiyatın e-İrsaliyesi varsa resmi görsel (entegratörün karekodlu çıktısı) varsayılan sekmedir. */
  despatchNumber?: string | null;
  onClose: () => void;
}

type Tab = "official" | "sheet";

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  print: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v7H6z" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
};

/** Blob yanıtındaki hata gövdesi (JSON ya da düz metin) → Türkçe mesaj. */
const blobError = async (e: any, fallback: string): Promise<string> => {
  const d = e?.response?.data;
  if (!e?.response) return "Sunucuya ulaşılamadı; bağlantıyı kontrol edip yeniden deneyin.";
  try {
    const text = d instanceof Blob ? await d.text() : typeof d === "string" ? d : "";
    if (text) {
      try { const j = JSON.parse(text); return j.detail || j.message || j.title || fallback; }
      catch { return text.length < 300 ? text : fallback; }
    }
  } catch { /* gövde okunamadı */ }
  return fallback;
};

/**
 * Yazdırma penceresi. e-İrsaliyesi olan sevkiyatta resmi görsel (platformdan, karekodlu) ile iç "Sevkiyat föyü" arasında
 * sekme vardır; e-İrsaliyesizde eskisi gibi yalnız iç irsaliye görünümü.
 * Resmi HTML (GİB XSLT çıktısı) karekodu betikle çizer: betik çalışır ama ayrı, opak kökenli bir çerçevede
 * (sandbox, allow-same-origin YOK) — oturuma ve jetona erişemez. Yazdır / Esc o çerçeveyle postMessage üzerinden konuşur.
 */
const BRIDGE_PRINT = "nedaris:print";
const BRIDGE_ESC = "nedaris:esc";
const BRIDGE = `<script>window.addEventListener("message",function(e){if(e.data==="${BRIDGE_PRINT}"){window.focus();window.print();}});`
  + `document.addEventListener("keydown",function(k){if(k.key==="Escape"){parent.postMessage("${BRIDGE_ESC}","*");}});</script>`;
const withBridge = (h: string) => (/<\/body>/i.test(h) ? h.replace(/<\/body>(?![\s\S]*<\/body>)/i, `${BRIDGE}</body>`) : h + BRIDGE);
const ShipmentPrintModal: React.FC<ShipmentPrintModalProps> = ({ shipmentId, number, despatchNumber, onClose }) => {
  const official = !!despatchNumber;
  const [tab, setTab] = useState<Tab>(official ? "official" : "sheet");
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const printRef = useRef<HTMLButtonElement>(null);
  const [opener] = useState(() => document.activeElement as HTMLElement | null);

  useEffect(() => {
    let live = true;
    setHtml(null); setError("");
    const load = tab === "official"
      ? shippingService.despatchFile(shipmentId, "html").then((b) => b.text())
      : shippingService.printHtml(shipmentId);
    load
      .then((h) => { if (live) setHtml(h); })
      .catch(async (e) => {
        const msg = tab === "official" ? await blobError(e, "e-İrsaliye görseli alınamadı.") : "Yazdırma görünümü alınamadı.";
        if (live) setError(msg);
      });
    return () => { live = false; };
  }, [shipmentId, attempt, tab]);

  useEffect(() => { if (html) printRef.current?.focus(); }, [html]);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Resmi görsel çerçevesinden gelen Esc (odak çerçevedeyken)
  useEffect(() => {
    const onMsg = (e: MessageEvent) => { if (e.data === BRIDGE_ESC && e.source === frameRef.current?.contentWindow) onClose(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onClose]);

  const print = () => {
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    if (tab === "official") w.postMessage(BRIDGE_PRINT, "*"); // ayrı köken: print() doğrudan çağrılamaz
    else { w.focus(); w.print(); }
  };
  // İç föy aynı kökende: Yazdır'dan sonra odak iframe'de kalır; Esc oradan da kapatsın
  const onFrameLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) =>
    e.currentTarget.contentDocument?.addEventListener("keydown", (k) => { if (k.key === "Escape") onClose(); });

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true); setError("");
    try {
      const blob = await shippingService.despatchFile(shipmentId, "pdf");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `e-Irsaliye-${despatchNumber || number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setError(await blobError(e, "e-İrsaliye PDF'i alınamadı.")); }
    finally { setDownloading(false); }
  };

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="po-modal wide sh-print" role="dialog" aria-modal="true" aria-labelledby="sh-print-title">
        <div className="po-modal-head">
          <div><span className="po-kicker">{number}{official ? ` · e-İrsaliye ${despatchNumber}` : ""}</span><h3 id="sh-print-title">{official && tab === "official" ? "e-İrsaliye" : official ? "Sevkiyat föyü" : "Sevk irsaliyesi"}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          {official && (
            <div className="po-chipgroup sh-print-tabs" role="tablist" aria-label="Görünüm">
              <button type="button" role="tab" aria-selected={tab === "official"} className={tab === "official" ? "on" : ""} onClick={() => setTab("official")}>e-İrsaliye (resmi)</button>
              <button type="button" role="tab" aria-selected={tab === "sheet"} className={tab === "sheet" ? "on" : ""} onClick={() => setTab("sheet")}>Sevkiyat föyü (iç)</button>
            </div>
          )}
          {!html && !error && <div className="po-loading"><span className="po-spin" /> {tab === "official" ? "e-İrsaliye görseli alınıyor" : "Yazdırma görünümü hazırlanıyor"}</div>}
          {error && <div className="po-note err" role="alert">{error} <button type="button" className="po-link" onClick={() => setAttempt((a) => a + 1)}>Yeniden dene</button></div>}
          {html && (
            tab === "official"
              // Entegratör/GİB çıktısı: betik (karekod) ayrı kökende çalışır; yazdırma penceresi için allow-modals
              ? <iframe ref={frameRef} className="sh-print-frame" title="e-İrsaliye" srcDoc={withBridge(html)} sandbox="allow-scripts allow-modals" />
              : <iframe ref={frameRef} className="sh-print-frame" title="Sevk irsaliyesi" srcDoc={html} onLoad={onFrameLoad} />
          )}
          {official && tab === "sheet" && <p className="po-hint">İç kullanım içindir; araçta resmi e-İrsaliye nüshası (karekodlu) bulunmalı.</p>}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint">{tab === "official" ? "Entegratörün karekodlu görseli · A4" : "A4 dikey · tarayıcı yazdırma penceresi açılır"}</span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" autoFocus onClick={onClose}>Kapat</button>
            {official && tab === "official" && <button type="button" className="po-btn" disabled={downloading} onClick={downloadPdf}>{I.download} {downloading ? "İndiriliyor" : "PDF indir"}</button>}
            <button type="button" ref={printRef} className="po-btn primary" disabled={!html} onClick={print}>{I.print} Yazdır</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentPrintModal;
